import { useEffect, useRef, useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Icon } from '@iconify/react'
import { Typography, Modal, Row, Tooltip, Popover, Menu } from 'antd'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Thumbs, Zoom } from 'swiper/modules'

// swiper styles
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/thumbs'
import 'swiper/css/zoom'

import {
    fetchTaskAttachmentsList,
    projectsState,
    resetTaskAttachments,
    deleteTaskAttachmentAction,
} from '../projects.slice'

import styles from './ProjectFiles.module.css'

const { Text } = Typography

// ─── helpers ────────────────────────────────────────────────────────────────

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp']
const VIDEO_EXTENSIONS = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'wmv']
const SHEET_EXTENSIONS = ['xls', 'xlsx', 'csv', 'ods']

const getExtension = (url = '') => {
    const clean = url.split('?')[0]
    return clean.split('.').pop().toLowerCase()
}

const isImage = (url) => IMAGE_EXTENSIONS.includes(getExtension(url))
const isVideo = (url) => VIDEO_EXTENSIONS.includes(getExtension(url))
const isSheet = (url) => SHEET_EXTENSIONS.includes(getExtension(url))

const getFileIcon = (url) => {
    const ext = getExtension(url)
    if (isImage(url)) return 'proicons:photo'
    if (isVideo(url)) return 'ep:video-play'
    if (isSheet(url)) return 'tabler:table'
    if (['pdf'].includes(ext)) return 'formkit:filepdf'
    if (['doc', 'docx'].includes(ext)) return 'f7:doc'
    if (['ppt', 'pptx'].includes(ext)) return 'hugeicons:ppt-02'
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext))
        return 'lsicon:file-zip-filled'
    return 'tabler:file'
}

const getFileName = (url = '') => {
    const clean = url.split('?')[0]
    return clean.split('/').pop() || 'Untitled File'
}

// ─── sub-components ─────────────────────────────────────────────────────────

const FileThumbnail = ({ url, fileName }) => {
    return (
        <div className={styles.thumbWrapper}>
            <img
                src={url}
                alt={fileName}
                className={styles.thumbImage}
                onError={(e) => {
                    e.target.style.display = 'none'
                    e.target.nextSibling.style.display = 'flex'
                }}
            />
            <div className={styles.thumbFallback} style={{ display: 'none' }}>
                <Icon icon="tabler:photo-off" width={32} color="#94a3b8" />
            </div>
        </div>
    )
}

const AttachmentActions = ({ onDownload, onDelete }) => {
    return (
        <Menu
            className="custom-menu-no-padding"
            style={{ padding: 0, margin: 0 }}
            items={[
                {
                    key: '1',
                    label: (
                        <div style={{ padding: '0 4px' }}>
                            Download attachment
                        </div>
                    ),
                    onClick: (e) => {
                        e.domEvent.stopPropagation()
                        onDownload()
                    },
                },
                {
                    key: '2',
                    danger: true,
                    label: (
                        <div style={{ padding: '0 4px' }}>
                            Delete attachment
                        </div>
                    ),
                    onClick: (e) => {
                        e.domEvent.stopPropagation()
                        onDelete()
                    },
                },
            ]}
        />
    )
}

const FileCard = ({
    attachment,
    index,
    onFileClick,
    onTaskClick,
    onDelete,
}) => {
    const url = attachment?.attachment || ''
    const fileName = getFileName(url)
    const taskTitle = attachment?.taskTitle || ''
    const taskId = attachment?.taskId

    const author = attachment?.commentUsername

    const fileType = isImage(url) ? 'image' : 'list'
    const icon = getFileIcon(url)

    const renderHeader = () => (
        <div className={styles.cardHeader}>
            <div className={styles.iconContainer}>
                <Icon icon={icon} width={20} color="#64748b" />
            </div>
            <div className={styles.headerInfo}>
                <Text className={styles.fileName} ellipsis>
                    {fileName}
                </Text>
                <Text className={styles.subTitle} ellipsis>
                    <span
                        className={styles.taskTitleLink}
                        onClick={(e) => {
                            e.stopPropagation()
                            taskId && onTaskClick && onTaskClick(taskId)
                        }}
                    >
                        {taskTitle}
                    </span>
                    {author && ` · ${author}`}
                </Text>
            </div>
            <div className={styles.actionMenu}>
                <Popover
                    content={
                        <AttachmentActions
                            onDownload={() => onFileClick(index)}
                            onDelete={() => onDelete && onDelete(attachment)}
                        />
                    }
                    trigger="click"
                    placement="bottomRight"
                    arrow={false}
                    overlayStyle={{ minWidth: 180 }}
                    styles={{
                        body: { padding: 0 },
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{ cursor: 'pointer', display: 'flex' }}
                    >
                        <Icon
                            icon="pepicons-pencil:dots-x"
                            width={20}
                            color="#64748b"
                        />
                    </div>
                </Popover>
            </div>
        </div>
    )

    if (fileType === 'image') {
        return (
            <div
                className={`${styles.card} ${styles.imageCard}`}
                onClick={() => onFileClick(index)}
            >
                {renderHeader()}
                <FileThumbnail url={url} fileName={fileName} />
            </div>
        )
    }

    return (
        <div
            className={`${styles.card} ${styles.listCard}`}
            onClick={() => onFileClick(index)}
        >
            <div className={styles.cardHeader}>
                <div className={styles.iconContainer}>
                    <Icon icon={icon} width={20} color="#64748b" />
                </div>
                <div className={styles.headerInfo}>
                    <Text className={styles.fileName} ellipsis>
                        {fileName}
                    </Text>
                    <Text className={styles.subTitle} ellipsis>
                        <span
                            className={styles.taskTitleLink}
                            onClick={(e) => {
                                e.stopPropagation()
                                taskId && onTaskClick && onTaskClick(taskId)
                            }}
                        >
                            {taskTitle}
                        </span>
                        {author && ` · ${author}`}
                    </Text>
                </div>
                <div className={styles.actionMenu}>
                    <Popover
                        content={
                            <AttachmentActions
                                onDownload={() => onFileClick(index)}
                                onDelete={() =>
                                    onDelete && onDelete(attachment)
                                }
                            />
                        }
                        trigger="click"
                        placement="bottomRight"
                        arrow={false}
                        overlayStyle={{ minWidth: 180 }}
                        styles={{
                            body: { padding: 0 },
                        }}
                    >
                        <div
                            onClick={(e) => e.stopPropagation()}
                            style={{ cursor: 'pointer', display: 'flex' }}
                        >
                            <Icon
                                icon="pepicons-pencil:dots-x"
                                width={20}
                                color="#64748b"
                            />
                        </div>
                    </Popover>
                </div>
            </div>
        </div>
    )
}

// ─── main component ──────────────────────────────────────────────────────────

const LIMIT = 10

const ProjectFiles = ({ projectId, onTaskClick }) => {
    const dispatch = useDispatch()

    const {
        taskAttachmentsList,
        taskAttachmentsIsNext,
        taskAttachmentsStatus,
        taskAttachmentsPage,
    } = useSelector(projectsState)

    const [isMediaOpen, setIsMediaOpen] = useState(false)
    const [activeIndex, setActiveIndex] = useState(0)
    const swiperRef = useRef(null)
    const prevRef = useRef(null)
    const nextRef = useRef(null)

    const sentinelRef = useRef(null)
    const isFetchingRef = useRef(false)

    // Sync swiper if list changes while open
    useEffect(() => {
        if (isMediaOpen && swiperRef.current) {
            swiperRef.current.slideTo(activeIndex)
        }
    }, [isMediaOpen, activeIndex])

    const handleFileClick = (index) => {
        setActiveIndex(index)
        setIsMediaOpen(true)
    }

    const handleDownload = async (url) => {
        try {
            const response = await fetch(url, { mode: 'cors' })
            if (!response.ok) throw new Error('Network response was not ok')
            const blob = await response.blob()
            const blobUrl = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = blobUrl
            a.download = getFileName(url) || 'download'
            document.body.appendChild(a)
            a.click()
            a.remove()
            window.URL.revokeObjectURL(blobUrl)
        } catch (err) {
            window.open(url, '_blank')
        }
    }

    const preparedFiles = taskAttachmentsList?.map((item) => {
        const url = item?.attachment || ''
        const ext = getExtension(url)
        let type = 'other'
        if (IMAGE_EXTENSIONS.includes(ext)) type = 'image'
        else if (VIDEO_EXTENSIONS.includes(ext)) type = 'video'
        else if (ext === 'pdf') type = 'pdf'
        else if (['ppt', 'pptx'].includes(ext)) type = 'pptx'

        return { src: url, type }
    })

    // Initial load + cleanup
    useEffect(() => {
        if (!projectId) return
        dispatch(resetTaskAttachments())
        dispatch(fetchTaskAttachmentsList({ projectId, skip: 0, limit: LIMIT }))
        return () => {
            dispatch(resetTaskAttachments())
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId])

    // Lazy load next page
    const loadMore = useCallback(() => {
        if (
            isFetchingRef.current ||
            !taskAttachmentsIsNext ||
            taskAttachmentsStatus === 'loading'
        )
            return

        isFetchingRef.current = true
        const skip = taskAttachmentsPage * LIMIT

        dispatch(
            fetchTaskAttachmentsList({ projectId, skip, limit: LIMIT })
        ).finally(() => {
            isFetchingRef.current = false
        })
    }, [
        dispatch,
        projectId,
        taskAttachmentsIsNext,
        taskAttachmentsPage,
        taskAttachmentsStatus,
    ])

    const handleDelete = (attachment) => {
        if (!attachment) return
        dispatch(
            deleteTaskAttachmentAction({
                taskId: attachment.taskId,
                type: attachment.type || 'taskAttachment',
                attachment: attachment.attachment,
            })
        )
    }

    // IntersectionObserver on sentinel
    useEffect(() => {
        const el = sentinelRef.current
        if (!el) return

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    loadMore()
                }
            },
            { threshold: 0.1 }
        )

        observer.observe(el)
        return () => observer.disconnect()
    }, [loadMore])

    const isInitialLoading =
        taskAttachmentsStatus === 'loading' && taskAttachmentsList?.length === 0

    // ── render ──────────────────────────────────────────────────────────────

    if (isInitialLoading) {
        return (
            <div className={styles.loadingContainer}>
                <Icon
                    icon="line-md:loading-loop"
                    style={{ fontSize: 48, color: '#6366f1' }}
                />
            </div>
        )
    }

    if (
        taskAttachmentsStatus !== 'loading' &&
        taskAttachmentsList?.length === 0
    ) {
        return (
            <div className={styles.centered}>
                <Icon icon="tabler:files-off" width={56} color="#cbd5e1" />
                <Text className={styles.emptyText}>No files attached yet</Text>
                <Text className={styles.emptySubText}>
                    Files added to tasks will appear here
                </Text>
            </div>
        )
    }

    const leftColumn = taskAttachmentsList?.filter((_, idx) => idx % 2 === 0)
    const rightColumn = taskAttachmentsList?.filter((_, idx) => idx % 2 !== 0)

    return (
        <div className={styles.container}>
            <div className={styles.grid}>
                <div className={styles.column}>
                    {leftColumn.map((attachment, idx) => (
                        <FileCard
                            key={attachment?._id || idx * 2}
                            attachment={attachment}
                            index={taskAttachmentsList?.indexOf(attachment)}
                            onFileClick={handleFileClick}
                            onTaskClick={onTaskClick}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>
                <div className={styles.column}>
                    {rightColumn.map((attachment, idx) => (
                        <FileCard
                            key={attachment?._id || idx * 2 + 1}
                            attachment={attachment}
                            index={taskAttachmentsList?.indexOf(attachment)}
                            onFileClick={handleFileClick}
                            onTaskClick={onTaskClick}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>
            </div>

            {/* Lazy-load sentinel */}
            <div ref={sentinelRef} className={styles.sentinel} />

            {/* Loading more spinner */}
            {taskAttachmentsStatus === 'loading' &&
                taskAttachmentsList?.length > 0 && (
                    <div className={styles.loadingMore}>
                        <Icon
                            icon="line-md:loading-loop"
                            style={{ fontSize: 24, color: '#6366f1' }}
                        />
                    </div>
                )}

            {/* Media Viewer Modal */}
            <Modal
                open={isMediaOpen}
                onCancel={(e) => {
                    e.stopPropagation()
                    setIsMediaOpen(false)
                }}
                footer={null}
                closeIcon={null}
                centered
                width="100vw"
                style={{
                    top: 0,
                    padding: 0,
                    maxWidth: '100vw',
                }}
                styles={{
                    header: {
                        backgroundColor: '#252628',
                        padding: '10px 24px',
                        borderRadius: 0,
                        borderBottom: '1px solid #4b4b4b',
                    },
                    body: {
                        padding: 0,
                        height: 'calc(100vh - 55px)',
                        backgroundColor: '#252628',
                    },
                    content: {
                        padding: 0,
                        margin: 0,
                        height: '100vh',
                        backgroundColor: '#252628',
                        borderRadius: 0,
                    },
                }}
                title={
                    <Row align={'middle'} justify="space-between">
                        <Row>
                            <Text style={{ color: '#fff', fontWeight: 200 }}>
                                {getFileName(
                                    taskAttachmentsList[activeIndex]?.attachment
                                )}
                            </Text>
                        </Row>
                        <Row
                            justify="end"
                            align="middle"
                            style={{ columnGap: '15px' }}
                        >
                            <Row
                                align="middle"
                                onClick={() =>
                                    handleDownload(
                                        taskAttachmentsList[activeIndex]
                                            ?.attachment
                                    )
                                }
                                style={{
                                    cursor: 'pointer',
                                    color: '#fff',
                                    columnGap: 6,
                                }}
                            >
                                <Icon
                                    icon="akar-icons:download"
                                    height={18}
                                    width={18}
                                />
                                <Text
                                    style={{ color: '#fff', fontWeight: 400 }}
                                >
                                    Download
                                </Text>
                            </Row>
                            <Tooltip title="Close" placement="top">
                                <Icon
                                    icon="akar-icons:cross"
                                    onClick={() => setIsMediaOpen(false)}
                                    height={18}
                                    width={18}
                                    style={{ cursor: 'pointer', color: '#fff' }}
                                />
                            </Tooltip>
                        </Row>
                    </Row>
                }
            >
                <div className="relative">
                    <div className="custom-swiper-buttons">
                        <div
                            ref={prevRef}
                            className="swiper-button-prev"
                            style={{ cursor: 'pointer' }}
                        >
                            <Icon icon="ph:arrow-left-bold" />
                        </div>
                        <div
                            ref={nextRef}
                            className="swiper-button-next"
                            style={{ cursor: 'pointer' }}
                        >
                            <Icon icon="ph:arrow-right-bold" />
                        </div>
                    </div>

                    <Swiper
                        style={{ height: '85vh' }}
                        modules={[Navigation, Thumbs, Zoom]}
                        navigation={{
                            nextEl: '.swiper-button-next',
                            prevEl: '.swiper-button-prev',
                        }}
                        zoom={{ maxRatio: 3 }}
                        onSwiper={(swiper) => {
                            swiperRef.current = swiper
                            setTimeout(() => {
                                if (
                                    prevRef.current &&
                                    nextRef.current &&
                                    swiperRef.current
                                ) {
                                    swiperRef.current.params.navigation.prevEl =
                                        prevRef.current
                                    swiperRef.current.params.navigation.nextEl =
                                        nextRef.current
                                    swiperRef.current.navigation.destroy()
                                    swiperRef.current.navigation.init()
                                    swiperRef.current.navigation.update()
                                }
                            }, 0)
                        }}
                        onSlideChange={(swiper) => {
                            setActiveIndex(swiper.activeIndex)
                        }}
                    >
                        {preparedFiles.map((file, index) => (
                            <SwiperSlide
                                key={index}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                {file.type === 'video' ? (
                                    <video
                                        src={file.src}
                                        controls
                                        style={{
                                            maxHeight: '80vh',
                                            maxWidth: '70%',
                                        }}
                                    />
                                ) : file.type === 'image' ? (
                                    <div className="swiper-zoom-container">
                                        <img
                                            src={file.src}
                                            alt={`slide-${index}`}
                                            style={{
                                                maxHeight: '80vh',
                                                maxWidth: '70%',
                                                objectFit: 'contain',
                                            }}
                                        />
                                    </div>
                                ) : file.type === 'pdf' ? (
                                    <iframe
                                        src={file.src}
                                        style={{
                                            width: '70%',
                                            height: '80vh',
                                            border: 'none',
                                        }}
                                        title={`pdf-${index}`}
                                    />
                                ) : file.type === 'pptx' ? (
                                    <iframe
                                        src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(file.src)}`}
                                        style={{
                                            width: '70%',
                                            height: '80vh',
                                            border: 'none',
                                        }}
                                        title={`pptx-${index}`}
                                    />
                                ) : (
                                    <div
                                        style={{
                                            color: 'white',
                                            fontSize: 18,
                                            textAlign: 'center',
                                        }}
                                    >
                                        File type not previewable
                                    </div>
                                )}
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            </Modal>
        </div>
    )
}

export default ProjectFiles
